import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, MessageCircle, Send, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export function SendPaymentLinkDialog({ ticket, pricing, hasWhatsApp, onSent }) {
  const [open, setOpen] = useState(false);
  const [pkg, setPkg] = useState("carnet_10h");
  const [sendWa, setSendWa] = useState(!!hasWhatsApp);
  const [name, setName] = useState(ticket?.reported_by || "");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(ticket?.whatsapp_from || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!pricing) return null;

  const amount = pkg === "single_hour"
    ? pricing.hourly_rate || 60
    : pricing.carnet_10h_price || 550;
  const hours = pkg === "single_hour" ? 1 : 10;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/hours/create-intent", {
        package: pkg,
        ticket_id: ticket.id,
        prefill_name: name || null,
        prefill_email: email || null,
        prefill_phone: phone || null,
        send_via_whatsapp: sendWa,
        app_url: window.location.origin,
      });
      setResult(data);
      if (data.wa_sent) {
        toast.success("Link inviato via WhatsApp al cliente");
      } else if (sendWa && data.wa_error) {
        toast.error(`Link generato ma WhatsApp KO: ${data.wa_error}`);
      } else {
        toast.success("Link di pagamento generato — copialo e invialo al cliente");
      }
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.public_url) return;
    try {
      await navigator.clipboard.writeText(result.public_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const reset = () => { setResult(null); setCopied(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm border-brand/30 text-brand hover:bg-brand-muted" data-testid="send-payment-link-btn">
          <MessageCircle className="h-4 w-4 mr-2" /> Invia link acquisto ore
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg" data-testid="send-payment-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Invia link acquisto ore</DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Pacchetto</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Card
                  className={`rounded-sm p-4 cursor-pointer border ${pkg === "single_hour" ? "border-brand bg-brand-muted/30" : "border-zinc-200"}`}
                  onClick={() => setPkg("single_hour")}
                  data-testid="link-pkg-single"
                >
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ora singola</div>
                  <div className="font-display text-2xl font-bold text-zinc-950">1 ora</div>
                  <div className="text-sm text-brand font-semibold">€{(pricing.hourly_rate || 60).toFixed(2)}</div>
                </Card>
                <Card
                  className={`rounded-sm p-4 cursor-pointer border ${pkg === "carnet_10h" ? "border-brand bg-brand-muted/30" : "border-zinc-200"}`}
                  onClick={() => setPkg("carnet_10h")}
                  data-testid="link-pkg-carnet"
                >
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Carnet 10h</div>
                  <div className="font-display text-2xl font-bold text-zinc-950">10 ore</div>
                  <div className="text-sm text-brand font-semibold">€{(pricing.carnet_10h_price || 550).toFixed(2)}</div>
                </Card>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Nome cliente (opzionale, pre-compila il form)</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" data-testid="link-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Email (opzionale)</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="mt-1 rounded-sm" data-testid="link-email" />
              </div>
              {hasWhatsApp && (
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendWa}
                    onChange={(e) => setSendWa(e.target.checked)}
                    className="mt-0.5"
                    data-testid="link-send-wa"
                  />
                  <span className="text-zinc-700">
                    Invia il link via WhatsApp al numero <span className="font-mono text-zinc-900">+{phone}</span>
                  </span>
                </label>
              )}
            </div>

            <div className="text-[11px] text-zinc-500 bg-zinc-50 p-3 rounded-sm">
              Il link genera una pagina pubblica dove il cliente compila i dati fiscali e paga con Stripe.
              Al completamento del pagamento le ore vengono accreditate automaticamente e il ticket sbloccato.
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-medium mb-1">Link generato</div>
              <div className="text-sm text-emerald-900">
                {result.wa_sent ? "✅ Il link è stato inviato via WhatsApp al cliente." : "Copia il link e invialo al cliente."}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">URL pubblico ({hours}h · €{amount.toFixed(2)})</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={result.public_url}
                  readOnly
                  className="rounded-sm font-mono text-xs"
                  data-testid="link-public-url"
                />
                <Button
                  variant="outline"
                  className="rounded-sm shrink-0"
                  onClick={copyLink}
                  data-testid="link-copy-btn"
                >
                  {copied ? <><Check className="h-3.5 w-3.5 mr-1" /> Copiato</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copia</>}
                </Button>
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                Scade il {new Date(result.expires_at).toLocaleString("it-IT")}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} disabled={busy}>Annulla</Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-brand hover:bg-brand-dark text-white rounded-sm"
                data-testid="link-submit-btn"
              >
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Genero...</> : <><Send className="h-4 w-4 mr-2" /> {sendWa ? "Genera e invia" : "Genera link"}</>}
              </Button>
            </>
          ) : (
            <Button onClick={() => setOpen(false)} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
              Chiudi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
